import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit
} from '@angular/core';
import { Subscription } from 'rxjs';

import { RxjsUtils } from '@therealmanage/ciranet-ui-common';

import { AuthService } from '../../_services/auth.service';
import { SurveyPopupService } from './survey-popup.service';

const SMCX_CONTAINER_ID = 'survey-popup-smcx-container';
const SMCX_SCRIPT_ID = 'smcx-sdk';
const ALLOWED_SURVEY_ORIGINS = ['https://widget.surveymonkey.com'];

@Component({
  selector: 'app-survey-popup',
  template: `
    <cira-modal
      [open]="visible"
      [title]="surveyTitle"
      size="lg"
      (closed)="dismiss()"
    >
      <div
        *ngIf="visible"
        id="survey-popup-smcx-container"
        class="survey-popup__container"
      ></div>
    </cira-modal>
  `,
  styleUrls: ['./survey-popup.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class SurveyPopupComponent implements OnInit, OnDestroy {
  visible = false;
  readonly surveyTitle = this.surveyPopupService.config.title;

  private subscriptions: Subscription[] = [];

  constructor(
    private authService: AuthService,
    private surveyPopupService: SurveyPopupService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    if (
      this.authService.isLoggedIn &&
      this.surveyPopupService.checkAndAdvanceSession()
    ) {
      this.triggerSurvey();
    }

    this.subscriptions.push(
      RxjsUtils.safeSubscribe(
        this.authService.getSubjectLoginSuccess(),
        (loggedIn: boolean) => {
          if (loggedIn && this.surveyPopupService.checkAndAdvanceSession()) {
            this.triggerSurvey();
          }
        }
      )
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  private triggerSurvey(): void {
    if (this.visible) return;
    setTimeout(() => {
      this.visible = true;
      this.cdr.markForCheck();
      this.loadSurveyScript();
    }, this.surveyPopupService.config.triggerDelayMs);
  }

  private isAllowedSrc(src: string): boolean {
    try {
      return ALLOWED_SURVEY_ORIGINS.includes(new URL(src).origin);
    } catch {
      return false;
    }
  }

  private loadSurveyScript(): void {
    setTimeout(() => {
      const src = this.surveyPopupService.config.scriptSrc;
      if (!this.isAllowedSrc(src)) {
        console.error('[SurveyPopup] Blocked untrusted scriptSrc:', src);
        return;
      }

      const container = document.getElementById(SMCX_CONTAINER_ID);
      if (!container || document.getElementById(SMCX_SCRIPT_ID)) return;

      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.async = true;
      script.id = SMCX_SCRIPT_ID;
      script.src = src;
      container.appendChild(script);
    });
  }

  private tearDownSurveyScript(): void {
    document.getElementById(SMCX_SCRIPT_ID)?.remove();
  }

  dismiss(): void {
    this.visible = false;
    this.cdr.markForCheck();
    this.surveyPopupService.markDismissed();
    this.tearDownSurveyScript();
  }
}
